import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";

/**
 * QuickCollectModal
 * Shown when an admin clicks "Generate QuickCollect Link" on the Area
 * Management page. Lets them choose whether submissions arriving through the
 * public form are auto-approved or held for manual review.
 *
 * See docs/QUICKCOLLECT_IMPLEMENTATION.md
 */
const QuickCollectModal = ({
  open,
  onClose,
  onGenerate,
  areaName,
  loading,
  initialAutoApprove = false,
}) => {
  const [mode, setMode] = useState(initialAutoApprove ? "auto" : "manual");

  useEffect(() => {
    if (!open) return;
    // Reset to the area's current setting each time the dialog opens.
    setMode(initialAutoApprove ? "auto" : "manual");
  }, [open, initialAutoApprove]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={titleStyles}>Generate QuickCollect Link</DialogTitle>
      <DialogContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            This creates a public link where guests can submit a QuickTale
            directly to
            <strong>{` ${areaName || "this area"}`}</strong>. Choose how those
            submissions should be handled.
          </Typography>

          <RadioGroup
            value={mode}
            onChange={(e) => setMode(e.target.value)}
            name="quickcollect-approval-mode"
          >
            <FormControlLabel
              value="auto"
              control={<Radio />}
              label="Auto Approve — submissions go live immediately"
              sx={labelStyles}
            />
            <FormControlLabel
              value="manual"
              control={<Radio />}
              label="Manually Approve — submissions wait for review"
              sx={labelStyles}
            />
          </RadioGroup>
        </Box>
      </DialogContent>
      <DialogActions sx={{ padding: "0px 24px 20px 24px" }}>
        <Button onClick={onClose} disabled={loading} sx={cancelButtonStyles}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={loading}
          onClick={() => onGenerate(mode === "auto")}
          sx={generateButtonStyles}
        >
          {loading ? "Generating..." : "Generate"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default QuickCollectModal;

const titleStyles = {
  fontFamily: "Avenir Book, sans-serif",
  fontWeight: 600,
  fontSize: 18,
};

const labelStyles = {
  "& .MuiFormControlLabel-label": {
    fontFamily: "Avenir Book, sans-serif",
    fontSize: 14,
  },
};

const cancelButtonStyles = {
  textTransform: "none",
  color: "#808080",
  fontFamily: "Avenir Book, sans-serif",
};

const generateButtonStyles = {
  textTransform: "none",
  background: "#00A7CD",
  fontFamily: "Avenir Book, sans-serif",
};
